import { useRegisterSW } from "virtual:pwa-register/react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "sonner";
import { watchForNewVersion } from "@/lib/app-updates";

/**
 * Registers the background worker on every page and keeps this copy of the site current. A new
 * version takes over by itself (the worker skips waiting), the page asks for one every 15 minutes
 * and when the tab comes back into view, and once one is ready the page reloads onto it as soon
 * as that is safe: when the person goes to another screen or leaves the tab. The message's Update
 * button does it right away.
 */
export function UpdatePrompt() {
  const registration = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const [tookOver, setTookOver] = useState(false);
  const location = useLocation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW: (_url, registered) => {
      registration.current = registered;
    },
    onRegisterError: (error) => console.warn("Service worker registration failed", error),
  });

  useEffect(
    () =>
      watchForNewVersion(
        () => registration.current,
        () => setTookOver(true),
      ),
    [],
  );

  const ready = tookOver || needRefresh;
  const reloadNow = () => {
    // A version still waiting is told to take over, and the page reloads once it has. If one has
    // already taken over there is nothing left to tell, so just reload onto it.
    if (needRefresh && !tookOver) void updateServiceWorker(true);
    else window.location.reload();
  };
  const reload = useRef(reloadNow);
  reload.current = reloadNow;

  // Remember which screen the person was on when the new version became ready.
  const here = useRef(location.pathname);
  here.current = location.pathname;
  const pathWhenReady = useRef(location.pathname);
  useEffect(() => {
    if (!ready) return;
    pathWhenReady.current = here.current;
    toast("A new version of GridAction is ready", {
      id: "app-update",
      duration: Number.POSITIVE_INFINITY,
      action: { label: "Update", onClick: () => reload.current() },
    });
  }, [ready]);

  // Going to another screen is a safe moment: nothing half-typed is lost.
  useEffect(() => {
    if (ready && location.pathname !== pathWhenReady.current) reload.current();
  }, [ready, location.pathname]);

  // So is leaving the tab: the next time it opens it is already the new one.
  useEffect(() => {
    if (!ready) return;
    const onHide = () => {
      if (document.visibilityState === "hidden") reload.current();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [ready]);

  return null;
}
